import FalconCardHeader from 'components/common/FalconCardHeader';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import React, { useMemo, useState } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { PieChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import BasicECharts from 'components/common/BasicEChart';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from 'providers/AppProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

const getOptions = (getThemeColor, chartData) => ({
  color: [
    getThemeColor('danger'),
    getThemeColor('success')
  ],
  tooltip: {
    padding: [7, 10],
    transitionDuration: 0,
    backgroundColor: getThemeColor('gray-100'),
    borderColor: getThemeColor('gray-300'),
    textStyle: { color: getThemeColor('gray-1100') },
    borderWidth: 1,
    formatter: params =>
      `<strong>${params.data.name}:</strong> ${params.data.value} (${params.percent}%)`
  },
  series: [
    {
      type: 'pie',
      radius: ['100%', '87%'],
      avoidLabelOverlap: false,
      emphasis: {
        scale: false
      },
      itemStyle: {
        borderWidth: 2,
        borderColor: getThemeColor('gray-100')
      },
      label: {
        show: true,
        position: 'center',
        formatter: () => `${chartData.reduce((a, b) => a + b.value, 0)}\nTotal`,
        fontSize: 16,
        fontWeight: '600',
        color: getThemeColor('gray-1100'),
        lineHeight: 20
      },
      data: chartData
    }
  ]
});

const LegendItem = ({ name, value, color, total, index }) => {
  const percentage = total > 0 ? ((value * 100) / total).toFixed(1) : 0;
  return (
    <Flex
      alignItems="center"
      justifyContent="between"
      className={`fw-semibold fs-11 ${index === 0 ? 'mt-2' : 'mt-1'}`}
    >
      <p className="mb-1">
        <FontAwesomeIcon icon="circle" className={`me-2 text-${color}`} />
        {name}
      </p>
      <span>{value} ({percentage}%)</span>
    </Flex>
  );
};

const OrderData = ({ data = [] }) => {
  const { getThemeColor } = useAppContext();
  const [selectedFacility, setSelectedFacility] = useState({ value: "ALL", label: "All Facilities" });

  const totals = useMemo(() => {
    return data.reduce(
      (acc, item) => {
        acc.unverified += item.no_of_unverified_prescriptions || 0;
        acc.verified += item.no_of_verified_prescriptions || 0;
        return acc;
      },
      { unverified: 0, verified: 0 }
    );
  }, [data]);

  const selectedData = useMemo(() => {
    if (selectedFacility.value === "ALL") return totals;
    const facility = data.find(f => f.facility_id === Number(selectedFacility.value));
    return {
      unverified: facility?.no_of_unverified_prescriptions || 0,
      verified: facility?.no_of_verified_prescriptions || 0
    };
  }, [selectedFacility, data, totals]);

  const chartData = useMemo(() => [
    { name: 'Unverified', value: selectedData.unverified },
    { name: 'Verified', value: selectedData.verified }
  ], [selectedData]);

  const total = selectedData.unverified + selectedData.verified;

  const legendItems = [
    { name: 'Unverified', value: selectedData.unverified, color: 'danger' },
    { name: 'Verified', value: selectedData.verified, color: 'success' }
  ];

  return (
    <Card className="h-100">
      <FalconCardHeader
        title="Prescription Status"
        light={false}
        titleTag="h6"
        className="pb-0"
      />

      <Card.Body className="pt-3">
        <Row className="mb-3">
          <Col>
            <ThemedSelect
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e)}
              options={[
                { value: "ALL", label: "All Facilities" },
                ...data.map(facility => ({
                  value: facility.facility_id,
                  label: facility.facility_name
                }))
              ]}
              placeholder="Select Facility"
            />
          </Col>
        </Row>
        <Row className="align-items-center justify-content-between g-0">
          <Col xs={5}>
            {legendItems.map((item, index) => (
              <LegendItem
                key={item.name}
                index={index}
                name={item.name}
                value={item.value}
                color={item.color}
                total={total}
              />
            ))}
          </Col>
          <Col xs="auto">
            <BasicECharts
              echarts={echarts}
              options={getOptions(getThemeColor, chartData)}
              style={{ width: '6.625rem', height: '6.625rem' }}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default OrderData;